import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useApiEntity } from '../../hooks/use-api-entity'
import { useModal } from '../../hooks/use-modal'
import { api } from '../../services/api'
import {
  PageHeader,
  FormField,
  Alert,
  ConfirmDialog,
  btnPrimary,
  btnSecondary,
  btnDanger,
  card,
  colors,
  input as inputStyle,
  label as labelStyle,
} from '../../components/ui'

interface ProviderForm {
  npi: string
  firstName: string
  lastName: string
  suffix: string
  specialty: string
  email: string
  phone: string
  fax: string
  resultDeliveryMethod: string
}

const defaults: ProviderForm = {
  npi: '',
  firstName: '',
  lastName: '',
  suffix: '',
  specialty: '',
  email: '',
  phone: '',
  fax: '',
  resultDeliveryMethod: '',
}

export function ProviderEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data, isNew, loading, saving, error, success, save, cancel, update, setError } = useApiEntity<ProviderForm>({
    path: '/providers',
    listPath: '/providers',
    defaultValue: defaults,
  })
  const archiveModal = useModal()
  const [archiving, setArchiving] = useState(false)
  const isSystem = !isNew && ((data as unknown as { isSystem?: boolean }).isSystem ?? false)

  const handleArchive = async () => {
    setArchiving(true)
    try {
      await api.delete(`/providers/${id}`)
      navigate('/providers')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive')
      archiveModal.close()
    } finally {
      setArchiving(false)
    }
  }

  if (loading) return <div style={{ color: colors.textMuted }}>Loading...</div>

  return (
    <div>
      <PageHeader
        title={isNew ? 'New Provider' : `${data.lastName}, ${data.firstName}${data.suffix ? ` ${data.suffix}` : ''}`}
        backTo="/settings/providers"
        backLabel="Back to Providers"
        actions={
          <>
            {!isNew && !isSystem && (
              <button onClick={archiveModal.open} style={btnDanger}>
                Archive
              </button>
            )}
            <button onClick={cancel} style={btnSecondary}>
              {isSystem ? 'Close' : 'Cancel'}
            </button>
            {!isSystem && (
              <button onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            )}
          </>
        }
      />
      {error && <Alert type="error" message={error} />}
      {success && <Alert type="success" message={success} />}
      {isSystem && (
        <div
          style={{
            padding: '0.625rem 0.875rem',
            marginBottom: '1rem',
            background: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '4px',
            fontSize: '0.875rem',
            color: '#92400e',
          }}
          data-testid="system-record-notice"
        >
          This is a system-provided provider and cannot be edited or archived.
        </div>
      )}

      <div style={{ ...card, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <FormField
          name="npi"
          label="NPI (10 digits)"
          required
          value={data.npi}
          onChange={(v) => update('npi', v)}
          disabled={!isNew || isSystem}
          placeholder="e.g. 1234567890"
        />
        <FormField
          name="firstName"
          label="First Name"
          required
          value={data.firstName}
          onChange={(v) => update('firstName', v)}
          disabled={isSystem}
        />
        <FormField
          name="lastName"
          label="Last Name"
          required
          value={data.lastName}
          onChange={(v) => update('lastName', v)}
          disabled={isSystem}
        />
        <FormField
          name="suffix"
          label="Suffix"
          value={data.suffix}
          onChange={(v) => update('suffix', v)}
          placeholder="e.g. MD, DO, NP"
          disabled={isSystem}
        />
        <FormField
          name="specialty"
          label="Specialty"
          value={data.specialty}
          onChange={(v) => update('specialty', v)}
          disabled={isSystem}
        />
        <FormField
          name="email"
          label="Email"
          type="email"
          value={data.email}
          onChange={(v) => update('email', v)}
          disabled={isSystem}
        />
        <FormField
          name="phone"
          label="Phone"
          value={data.phone}
          onChange={(v) => update('phone', v)}
          disabled={isSystem}
        />
        <FormField
          name="fax"
          label="Fax"
          value={data.fax}
          onChange={(v) => update('fax', v)}
          placeholder="Fax number"
          disabled={isSystem}
        />
        <div>
          <label htmlFor="resultDeliveryMethod" style={labelStyle}>
            Result Delivery
          </label>
          <select
            id="resultDeliveryMethod"
            value={data.resultDeliveryMethod}
            onChange={(e) => update('resultDeliveryMethod', e.target.value)}
            style={inputStyle}
            disabled={isSystem}
          >
            <option value="">Use site default</option>
            <option value="fax">Fax</option>
            <option value="email">Email</option>
            <option value="ehr">EHR</option>
            <option value="none">None</option>
          </select>
        </div>
      </div>

      <ConfirmDialog
        isOpen={archiveModal.isOpen}
        onClose={archiveModal.close}
        onConfirm={handleArchive}
        title="Archive Provider"
        message={`Are you sure you want to archive ${data.firstName} ${data.lastName}? They will no longer appear in provider selections.`}
        confirmLabel="Archive"
        loading={archiving}
      />
    </div>
  )
}
